import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StaffShell } from '../../components/StaffShell';
import { PatientAvatar } from '../../components/PatientAvatar';
import { ResultReleaseCard } from '../../components/ResultReleaseCard';
import { LabUploadHistory } from '../../components/LabUploadHistory';
import { EditPatientModal } from '../../components/EditPatientModal';
import { ClinicianBiomarkerUpload } from '../../components/ClinicianBiomarkerUpload';
import { useUserStore } from '../../store/userStore';
import { getPatientRecord } from '../../lib/api';

const TABS = [
  { key: 'results', label: 'Results & visibility', icon: 'fa-vial' },
  { key: 'uploads', label: 'Uploads', icon: 'fa-cloud-arrow-up' },
  { key: 'activity', label: 'Activity', icon: 'fa-clock-rotate-left' },
];

const ACTION_LABEL = { create: 'Created', update: 'Updated', view: 'Viewed', release: 'Released results', hold: 'Held results', login: 'Signed in' };

function ActivityList({ items }) {
  if (!items?.length) return <p className="text-white/40 text-sm">No activity recorded yet.</p>;
  return (
    <div className="divide-y divide-white/10" data-testid="record-activity">
      {items.map((a, i) => (
        <div key={a.id || i} className="py-2.5 text-sm flex items-start gap-3">
          <div className="w-2 h-2 mt-2 rounded-full bg-[var(--aura-purple-light)] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-white">
              <span className="font-medium">{a.user_name || a.user_email || a.user_role}</span>{' '}
              <span className="text-white/70">{ACTION_LABEL[a.action] || a.action} {a.resource_type?.replace(/_/g, ' ')}</span>
            </div>
            <div className="text-xs text-white/40">{a.timestamp ? new Date(a.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PatientRecord() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const clinician = useUserStore((s) => s.user);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('results');
  const [editing, setEditing] = useState(false);

  const load = () => getPatientRecord(clinician.id, patientId).then(setData).catch(() => {});
  useEffect(() => { if (clinician?.id) load(); }, [clinician?.id, patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <StaffShell title="Clinician"><div className="text-white/50 p-10 text-center"><i className="fa-solid fa-circle-notch fa-spin" /></div></StaffShell>;

  const p = data.patient;
  const held = data.results.filter((r) => !r.released).length;

  return (
    <StaffShell title="Clinician" badge="Patient record">
      {editing && (
        <EditPatientModal
          clinicianId={clinician.id}
          patient={p}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
      <button onClick={() => navigate(`/clinician/patient/${patientId}`)} className="text-white/60 hover:text-white text-sm flex items-center gap-2 mb-4"><i className="fa-solid fa-arrow-left" /> Back to clinical review</button>

      <div className="glass-dark rounded-2xl p-5 sm:p-6 mb-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="record-header">
        <PatientAvatar patient={p} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl text-white truncate" data-testid="record-patient-name">{p.name}</h1>
            <button data-testid="record-edit-patient-btn" onClick={() => setEditing(true)} className="grid place-items-center w-8 h-8 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-base"><i className="fa-solid fa-pen text-xs" /></button>
          </div>
          <div className="text-white/50 text-sm">
            Age {p.age} · <span className="capitalize">{p.stage}</span>{p.date_of_birth ? ` · DOB ${p.date_of_birth}` : ''}{p.phone ? ` · ${p.phone}` : ''}
          </div>
          <div className="text-white/50 text-sm truncate">{p.email?.endsWith('@patients.auralis.local') ? 'No login email on record' : p.email}</div>
          {p.primary_concerns?.length ? (
            <div className="flex flex-wrap gap-1.5 mt-2">{p.primary_concerns.map((c) => <span key={c} className="text-[11px] rounded-full px-2 py-0.5 bg-white/10 text-white/70">{c}</span>)}</div>
          ) : null}
          {p.wellbeing_statement ? (
            <p className="text-white/70 text-sm italic mt-3 line-clamp-3" data-testid="record-wellbeing-statement">“{p.wellbeing_statement}”</p>
          ) : null}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <div className="text-xs rounded-xl px-3 py-2 bg-white/5 border border-white/10 text-white/70" data-testid="record-release-summary">
            <div><span className="text-white font-semibold">{data.results.length}</span> results panel{data.results.length === 1 ? '' : 's'}</div>
            <div className={held ? 'text-amber-300' : 'text-emerald-300'}>{held ? `${held} held from patient` : 'All released'}</div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="record-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`record-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`text-sm rounded-full px-4 py-2 whitespace-nowrap border transition-base ${tab === t.key ? 'bg-[rgba(168,85,247,0.3)] border-[rgba(168,85,247,0.6)] text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
          >
            <i className={`fa-solid ${t.icon} mr-2 opacity-70`} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'results' && (
        <div data-testid="record-results-tab">
          <div className="glass-dark rounded-2xl p-4 mb-4 flex items-start gap-3 border border-amber-500/20">
            <i className="fa-solid fa-shield-heart text-amber-300 mt-0.5" />
            <p className="text-sm text-white/70">New results are <span className="text-white font-medium">held from the patient by default</span>. Review each panel, hold back any individual values you'd rather discuss in person, add a reassuring note, then release.</p>
          </div>
          <div className="glass-dark rounded-2xl p-5 mb-4">
            <h3 className="font-display text-lg text-white mb-3 flex items-center gap-2"><i className="fa-solid fa-wand-magic-sparkles text-[var(--aura-purple-light)]" /> Add results</h3>
            <ClinicianBiomarkerUpload patientId={patientId} onSaved={load} onUploaded={load} />
          </div>
          {data.results.length === 0 ? (
            <p className="text-white/40 text-sm" data-testid="record-no-results">No results on record yet.</p>
          ) : (
            data.results.map((r) => (
              <ResultReleaseCard key={r.id + (r.release_updated_at || '')} clinicianId={clinician.id} patientId={patientId} result={r} ranges={data.reference_ranges} onUpdated={load} />
            ))
          )}
        </div>
      )}

      {tab === 'uploads' && (
        <div className="glass-dark rounded-2xl p-5" data-testid="record-uploads-tab">
          <LabUploadHistory uploads={data.uploads} />
        </div>
      )}

      {tab === 'activity' && (
        <div className="glass-dark rounded-2xl p-5" data-testid="record-activity-tab">
          <ActivityList items={data.activity} />
        </div>
      )}
    </StaffShell>
  );
}
